import React, {
	useRef,
	useState,
	forwardRef,
	useImperativeHandle,
	useCallback,
	useEffect
} from 'react';
import { Formik, Form, FieldArray, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { observer } from 'mobx-react';
import { jobPositionStore } from '../JobPositionStore';
import { notificationStore } from 'components/layout/notifications/NotificationStore';
import { formatTimeAgo } from 'shared/utils';
import { AssessmentsDropdown } from 'components/layout/form-fields/inputs/AssessmentsDropdown';
import UserProfileAvatar from 'ats/components/user-profile-avatar/UserProfileAvatar';
import MultiItemsList from 'ats/components/multi-items-list/MultiItemsList';

const validationSchema = Yup.object({
	tests: Yup.array().of(Yup.object()),
	budgets: Yup.array().of(Yup.string()),
	additionalInfos: Yup.array().of(Yup.string())
});

function FormikStateBridge({
	isSubmitting,
	dirty,
	onSubmittingChange,
	onDirtyChange
}) {
	useEffect(() => {
		onSubmittingChange?.(isSubmitting);
	}, [isSubmitting, onSubmittingChange]);

	useEffect(() => {
		onDirtyChange?.(dirty);
	}, [dirty, onDirtyChange]);

	return null;
}

const JobPositionInsightsFormInner = forwardRef(
	function JobPositionInsightsForm(
		{
			data,
			onSuccess,
			isEditing: isEditingProp,
			onIsEditingChange,
			onSubmittingChange,
			onDirtyChange
		},
		ref
	) {
		const isControlled = typeof onIsEditingChange === 'function';
		const [internalEditing, setInternalEditing] = useState(false);
		const [isDirty, setIsDirty] = useState(false);
		const [isSubmitting, setIsSubmitting] = useState(false);
		const isEditing = isControlled ? Boolean(isEditingProp) : internalEditing;
		const setEditing = next => {
			if (isControlled) {
				onIsEditingChange(next);
			} else {
				setInternalEditing(next);
			}
			if (!next) {
				setIsDirty(false);
				setIsSubmitting(false);
			}
		};

		const cancelEdit = useCallback(() => {
			if (isControlled) {
				onIsEditingChange(false);
			} else {
				setInternalEditing(false);
			}
			setIsDirty(false);
			setIsSubmitting(false);
		}, [isControlled, onIsEditingChange]);

		const handleDirtyChange = useCallback(
			dirty => {
				setIsDirty(dirty);
				onDirtyChange?.(dirty);
			},
			[onDirtyChange]
		);

		const handleSubmittingChange = useCallback(
			submitting => {
				setIsSubmitting(submitting);
				onSubmittingChange?.(submitting);
			},
			[onSubmittingChange]
		);

		const formikRef = useRef(null);

		useImperativeHandle(
			ref,
			() => ({
				submit: () => formikRef.current?.submitForm(),
				cancelEdit
			}),
			[cancelEdit]
		);

		const mapTestsToFormValues = tests =>
			(tests || []).map(test =>
				typeof test === 'string' ? { id: test, name: test } : test
			);

		const initialData = {
			tests: mapTestsToFormValues(data?.tests),
			budgets: data?.budgets || [],
			additionalInfos: data?.additionalInfos || []
		};

		const handleSubmit = async values => {
			const result = await jobPositionStore.update(data.id, {
				job: {
					budgets: values.budgets,
					additionalInfos: values.additionalInfos,
					tests: values.tests
				}
			});

			if (result.status === 'success') {
				notificationStore.pushSuccessNotification(
					'Job Position Updated',
					'The Job Position has been successfully updated.'
				);
			} else {
				notificationStore.pushErrorNotification(
					'Update Failed',
					'Failed to update the Job Position'
				);
			}
		};

		return (
			<div className="JobPositionInsightsForm">
				{!isControlled && (
					<div className="job-position-form-header">
						<div className="is-flex gap-1">
							<UserProfileAvatar
								imageUrl={data?.updatedBy?.imageUrl}
								name={data?.updatedBy?.name}
							/>
							<span>
								Updated:{' '}
								{data?.updatedAt
									? formatTimeAgo(new Date(data?.updatedAt))
									: null}
							</span>
						</div>
						<div className="action-buttons">
							{isEditing ? (
								<>
									<button
										type="button"
										onClick={() => setEditing(false)}
										className="cancel-btn">
										Cancel
									</button>
									<button
										type="button"
										disabled={!isDirty || isSubmitting}
										onClick={() => formikRef.current.submitForm()}
										className="save-btn">
										Save
									</button>
								</>
							) : (
								<button
									type="button"
									onClick={() => setEditing(true)}
									className="edit-btn">
									<img src="/ats/icons/pencil-blue-icon.svg" alt="" />
								</button>
							)}
						</div>
					</div>
				)}
				{isEditing ? (
					<Formik
						innerRef={formikRef}
						initialValues={initialData}
						validationSchema={validationSchema}
						onSubmit={async (values, actions) => {
							await handleSubmit(values);
							onSuccess && onSuccess();
							setEditing(false);
							actions.setSubmitting(false);
						}}>
						{({ setFieldValue, values, isSubmitting, dirty }) => (
							<Form className="job-position-form-content">
								<FormikStateBridge
									isSubmitting={isSubmitting}
									dirty={dirty}
									onSubmittingChange={handleSubmittingChange}
									onDirtyChange={handleDirtyChange}
								/>
								{/* Tests */}
								<div className="columns">
									<div className="column is-full">
										<div className="field has-text-left">
											<label className="subtitle is-7">Tests</label>
											<div className="control">
												<AssessmentsDropdown
													onValueChange={values => {
														setFieldValue('tests', values);
													}}
													value={values.tests}
													isMulti
													placeholder=""
												/>
											</div>
											<ErrorMessage
												name="tests"
												component="div"
												className="text-red-600 text-sm"
											/>
										</div>
									</div>
								</div>

								{/* Budgets */}
								<div className="columns">
									<div className="column is-full">
										<div className="field has-text-left">
											<label className="subtitle is-7">Budget</label>
											<FieldArray name="budgets">
												{({ push, remove, replace }) => (
													<MultiItemsList
														items={values.budgets}
														push={push}
														remove={remove}
														replace={replace}
														inputPrefix="$"
														errorName="budgets"
													/>
												)}
											</FieldArray>
										</div>
									</div>
								</div>

								{/* Additional Infos */}
								<div className="columns">
									<div className="column is-full">
										<div className="field has-text-left">
											<label className="subtitle is-7">Additional Info</label>
											<FieldArray name="additionalInfos">
												{({ push, remove, replace }) => (
													<MultiItemsList
														items={values.additionalInfos}
														push={push}
														remove={remove}
														replace={replace}
														errorName="additionalInfos"
													/>
												)}
											</FieldArray>
										</div>
									</div>
								</div>
							</Form>
						)}
					</Formik>
				) : (
					<div className="job-position-info-content">
						<div className="job-position-info-container">
							<h3>Tests</h3>
							<div className="tags-container">
								{initialData.tests.map(test => (
									<span key={test.id ?? test.name}>{test.name}</span>
								))}
							</div>
						</div>

						<div className="job-position-info-container">
							<h3>Budget</h3>
							<ul>
								{initialData.budgets.map((budget, i) => (
									<li key={i}>{budget}</li>
								))}
							</ul>
						</div>

						<div className="job-position-info-container">
							<h3>Additional Info</h3>
							<ul>
								{initialData.additionalInfos.map((addInfo, i) => (
									<li key={i}>{addInfo}</li>
								))}
							</ul>
						</div>
					</div>
				)}
			</div>
		);
	}
);

const JobPositionInsightsForm = observer(JobPositionInsightsFormInner);
export default JobPositionInsightsForm;
